import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Button, H3, LayoutSidebar, SharedPOSBreakdown, SidebarButton, SidebarHeaderTitle, SidebarTitle, Text } from 'components';
import { theme, toRem } from 'design';
import { Size } from 'design/types';
import { MPContent, SidebarMobileActions } from 'screens/MakePayment/components';
import { selectSiteSettings } from 'store/Site';
import { selectCalculatorPaymentSet, selectCalculatorPOSResults } from 'store/TipCalculator';
import { ICalculatorRules, IPOSResults, TicketOverrideType } from 'types';
import { buildSortedChecksList, centsToDollarsDisplay, centsToNegativeDollarsDisplay, getSharedPOSJobTitles } from 'utils';

interface ISharedPosProps {
  currentCalculatorRules: ICalculatorRules;
  onBack: () => void;
  onExpand: () => void;
  pageHeaderContent: React.ReactNode;
  sidebarOpen?: boolean;
}

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${toRem(16)} ${toRem(40)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: ${toRem(40)};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${toRem(24)};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const SummaryContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;

  p {
    font-size: ${toRem(14)};
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(11)};

  h3 {
    font-size: ${toRem(24)};
  }
`;

const SummaryTotals = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(24)};
`;

const SummaryLineItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${toRem(8)};

  p {
    color: ${theme.color.neutral[500]};
  }

  &:last-child {
    margin-bottom: 0px;
    margin-top: ${toRem(24)};

    p {
      color: ${theme.color.neutral[900]};
    }
  }
`;

const SharedPOS = ({ currentCalculatorRules, onBack, onExpand, pageHeaderContent, sidebarOpen }: ISharedPosProps) => {
  const sharedPosData = useSelector(selectCalculatorPOSResults())?.data?.filter((data) => !data.employee && getSharedPOSJobTitles(currentCalculatorRules).includes(data.provider.schedules[0]?.jobTitle.toLowerCase())) || [];
  const { tipCalculatorPercentFee: creditCardFee } = useSelector(selectSiteSettings()) || {};
  const paymentSetDate = useSelector(selectCalculatorPaymentSet())?.date;
  const formattedDate = dayjs(paymentSetDate).format('MMMM Do, YYYY');

  const renderSummaryContent = useMemo(() => {
    // calculate the totals for share
    let cashTips = 0;
    let cashAutograts = 0;
    let ccTips = 0;
    let ccAutograts = 0;
    let ccFeeSum = 0;
    let net = 0;

    sharedPosData.forEach((pos) => {
      pos.segments.forEach((segment) => {
        cashTips += segment.totals.tip.cash;
        cashAutograts += segment.totals.autoGratuity.cash;
        ccTips += segment.totals.tip.creditCard;
        ccAutograts += segment.totals.autoGratuity.creditCard;
        ccFeeSum += segment.totals.tip.creditCardFee + segment.totals.autoGratuity.creditCardFee;
      });
    });

    // Get tip total of split checks
    const splitChecks = buildSortedChecksList({ data: sharedPosData } as IPOSResults).filter((check) => check.override?.type === TicketOverrideType.SPLIT);
    const splitChecksTotal = splitChecks.reduce((acc, check) => acc + check.totals.tip.net + check.totals.autoGratuity.net, 0); // Split check total

    // Get tip total of ignored checks
    const ignoredChecks = buildSortedChecksList({ data: sharedPosData } as IPOSResults).filter((check) => check.override?.type === TicketOverrideType.IGNORE);
    const ignoredChecksTotal = ignoredChecks.reduce((acc, check) => acc + check.totals.tip.net + check.totals.autoGratuity.net, 0); // Ignore check total

    net += (cashTips || 0) + (cashAutograts || 0) + (ccTips || 0) + (ccAutograts || 0) + (splitChecksTotal || 0) - (ignoredChecksTotal || 0) - ccFeeSum;

    return (
      <SummaryContent>
        <SummaryHeader>
          <H3 marginBottom={24}>{formattedDate}</H3>
          <Text fontWeight="medium">Shared POS Details</Text>
        </SummaryHeader>
        <SummaryTotals>
          <SummaryLineItem data-cy="shared-pos-cc-tips-total">
            <Text>Credit Card Tips</Text>
            <Text>{centsToDollarsDisplay(ccTips)}</Text>
          </SummaryLineItem>
          <SummaryLineItem data-cy="shared-pos-cc-autograts-total">
            <Text>Credit Card Autograts</Text>
            <Text>{centsToDollarsDisplay(ccAutograts)}</Text>
          </SummaryLineItem>
          <SummaryLineItem data-cy="shared-pos-cash-tips-total">
            <Text>Cash Tips</Text>
            <Text>{centsToDollarsDisplay(cashTips)}</Text>
          </SummaryLineItem>
          <SummaryLineItem data-cy="shared-pos-cash-autograts-total">
            <Text>Cash Autograts</Text>
            <Text>{centsToDollarsDisplay(cashAutograts)}</Text>
          </SummaryLineItem>
          {!!creditCardFee && (
            <SummaryLineItem data-cy="shared-pos-cc-fees-total">
              <Text>Credit Card Fees {`(${creditCardFee}%)`}</Text>
              <Text>{centsToNegativeDollarsDisplay(ccFeeSum)}</Text>
            </SummaryLineItem>
          )}
          {splitChecksTotal > 0 && (
            <SummaryLineItem data-cy="shared-pos-split-checks-total">
              <Text>Split Checks</Text>
              <Text>{centsToDollarsDisplay(splitChecksTotal)}</Text>
            </SummaryLineItem>
          )}
          {ignoredChecksTotal > 0 && (
            <SummaryLineItem data-cy="shared-pos-ignored-checks-total">
              <Text>Ignored Checks</Text>
              <Text>{centsToNegativeDollarsDisplay(ignoredChecksTotal)}</Text>
            </SummaryLineItem>
          )}
          <SummaryLineItem data-cy="shared-pos-net-total">
            <Text fontWeight="semibold">Net after deductions</Text>
            <Text fontWeight="semibold">{centsToDollarsDisplay(net)}</Text>
          </SummaryLineItem>
        </SummaryTotals>
      </SummaryContent>
    );
  }, [sharedPosData]);

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}
          <MPContent data-cy="shared-pos-list">
            {sharedPosData?.map((posData) => (
              <SharedPOSBreakdown key={`shared-pos-${posData.provider.employeeId}`} calculatorRules={currentCalculatorRules.rules} categoryGroups={currentCalculatorRules.categoryGroups} posData={posData} />
            ))}
          </MPContent>
        </>
      }
      sidebar={
        <Summary data-cy="shared-pos-summary">
          <MobileHeader>
            <SidebarHeaderTitle>
              <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
              <SidebarTitle title="Shared POS Details" />
            </SidebarHeaderTitle>
            <SidebarMobileActions columns={2}>
              <Button data-cy="shared-pos-back-button" label="Back" type="button" appearance="white" size="small" onClick={onBack} />
            </SidebarMobileActions>
          </MobileHeader>
          {renderSummaryContent}
          <Button data-cy="shared-pos-back-button" label="Back" type="button" appearance="white" size={Size.LARGE} onClick={onBack} />
        </Summary>
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default SharedPOS;
